import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Paper,
  Divider,
  Chip,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Cloud as EndpointIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

import { configAPI } from '../services/api';
import EndpointManagement from './EndpointManagement';

const ConfigurationPanel = ({ showNotification }) => {
  const [tabValue, setTabValue] = useState(0);
  const [endpointConfig, setEndpointConfig] = useState({
    endpoint_url: '',
    port: null,
    use_ssl: false,
    timeout: 30,
  });
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testStatus, setTestStatus] = useState(null);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      const [endpoint, appSettings] = await Promise.all([
        configAPI.getEndpoint(),
        configAPI.getSettings(),
      ]);
      setEndpointConfig(endpoint);
      setSettings(appSettings);
    } catch (error) {
      showNotification('Failed to load configuration', 'error');
      console.error('Load error:', error);
    }
  };

  const handleEndpointChange = (field, value) => {
    setEndpointConfig({ ...endpointConfig, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await configAPI.updateEndpoint(endpointConfig);
      showNotification('Configuration saved successfully', 'success');
      setTestStatus(null);
    } catch (error) {
      showNotification('Failed to save configuration', 'error');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTestStatus('testing');
    try {
      const health = await configAPI.healthCheck();
      if (health.status === 'healthy') {
        setTestStatus('success');
        showNotification('Connection test successful', 'success');
      } else {
        setTestStatus('error');
        showNotification('Connection test failed', 'error');
      }
    } catch (error) {
      setTestStatus('error');
      showNotification('Connection test failed', 'error');
      console.error('Test error:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Configuration
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            icon={<EndpointIcon />} 
            label="Endpoints" 
            iconPosition="start"
            sx={{ minHeight: { xs: 48, sm: 64 } }}
          />
          <Tab 
            icon={<SettingsIcon />} 
            label="Simulation Settings" 
            iconPosition="start"
            sx={{ minHeight: { xs: 48, sm: 64 } }}
          />
        </Tabs>
      </Box>

      {/* Endpoints Tab */}
      {tabValue === 0 && (
        <EndpointManagement showNotification={showNotification} />
      )}

      {/* Settings Tab */}
      {tabValue === 1 && settings && (
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Simulation Parameters
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Current simulation settings (read-only)
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">
                      ECG Sampling Rate
                    </Typography>
                    <Typography variant="h6">
                      {settings.sampling_rate} samples
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">
                      ECG Frequency
                    </Typography>
                    <Typography variant="h6">
                      {settings.ecg_frequency} Hz
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">
                      Vital Signs Interval
                    </Typography>
                    <Typography variant="h6">
                      {settings.vital_interval}s
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="textSecondary">
                      Alarm Probability
                    </Typography>
                    <Typography variant="h6">
                      {(settings.alarm_probability * 100).toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Data Generation Ranges
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(settings.data_ranges || {}).map(([key, range]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {key.replace(/_/g, ' ').toUpperCase()}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        <Chip label={`Min: ${range.min}`} size="small" />
                        <Typography variant="body2">-</Typography>
                        <Chip label={`Max: ${range.max}`} size="small" />
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Example Data Formats */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Example Data Formats
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Sample JSON payloads sent to destination endpoints
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  ECG Waveform (35 samples)
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.75rem' }}>
{`{
  "Key": "EcgI",
  "Value": "-3,-4,-3,-3,-3,-3,...,2,3,5",
  "SerialNumber": "DGT7-SIM0001",
  "DeviceTypeId": 1
}`}
                  </pre>
                </Paper>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  SpO2 Waveform (13 samples)
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.75rem' }}>
{`{
  "Key": "Spo2",
  "Value": "98,98,99,98,98,97,98,99,98,98,98,98,98",
  "SerialNumber": "DGT7-SIM0001",
  "DeviceTypeId": 1
}`}
                  </pre>
                </Paper>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Vital Signs (15 parameters)
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.75rem' }}>
{`{
  "Key": "vital",
  "Value": [
    {
      "TrendName": "HR",
      "Value": 75,
      "DeviceValue": 75,
      "Unit": "bpm",
      "dateTime": "14:25:33"
    }
    // ... 14 more parameters
  ],
  "SerialNumber": "DGT7-SIM0001",
  "DeviceTypeId": 1
}`}
                  </pre>
                </Paper>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Alarm/Event
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '0.75rem' }}>
{`{
  "Key": "alarm",
  "Value": {
    "time": "5:32:10 pm",
    "data": "OPERATING MODE: Monitor Advanced"
  },
  "SerialNumber": "DGT7-SIM0001",
  "DeviceTypeId": 1
}`}
                  </pre>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default ConfigurationPanel;
